'use client'
import { type Dispatch, type SetStateAction, useState, useEffect } from 'react'
import { type ProductFragment } from '@/lib/types/datocms/graphql/generated'
import Image from 'next/image'
import Toggle from '@/components/ui/Toggle'
import Icon from '@/components/svg/icon'
import Modal from '@/components/Modal'
import CarouselInModal from '@/components/CarouselInModal'
import clsx from 'clsx'
import IconButton from '@/components/ui/IconButton'

export default function ProductGallery({
	product,
	setIsImageModalOpen,
	isImageModalOpen,
}: {
	product: ProductFragment
	setIsImageModalOpen: Dispatch<SetStateAction<boolean>>
	isImageModalOpen: boolean
}) {
	const [activeImageIndex, setActiveImageIndex] = useState(1)
	const [activeGalleryType, setActiveGalleryType] = useState<string[]>(['Unloaded'])

	// Set initial active image index based on screen size
	useEffect(() => {
		const mediaQuery = window.matchMedia('(min-width: 768px)')
		const handleScreenSize = (e: MediaQueryListEvent | MediaQueryList): void => {
			setActiveImageIndex(e.matches ? 0 : 1)
		}

		// Initial check
		handleScreenSize(mediaQuery)

		// Listen for changes
		mediaQuery.addEventListener('change', handleScreenSize)

		return () => {
			mediaQuery.removeEventListener('change', handleScreenSize)
		}
	}, [])

	// Determine which gallery to use
	const galleryImages =
		product.loaded?.length > 0 && product.unloaded?.length > 0
			? activeGalleryType[0] === 'Loaded'
				? product.loaded
				: product.unloaded
			: product.gallery || []

	// Combine coverImage with gallery images
	const currentGallery = product.coverImage?.[0]
		? [product.coverImage?.[0], ...galleryImages]
		: galleryImages

	const updateImageIndex = (currentIndex: number, direction: 'next' | 'prev') => {
		const lastIndex = currentGallery.length - 1
		if (direction === 'next') {
			return currentIndex === lastIndex ? 0 : currentIndex + 1
		} else {
			return currentIndex === 0 ? lastIndex : currentIndex - 1
		}
	}

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'ArrowRight') {
				setActiveImageIndex((prevIndex) => updateImageIndex(prevIndex, 'next'))
			} else if (event.key === 'ArrowLeft') {
				setActiveImageIndex((prevIndex) => updateImageIndex(prevIndex, 'prev'))
			}
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => {
			window.removeEventListener('keydown', handleKeyDown)
		}
	}, [currentGallery])

	return (
		<div className="product-gallery relative pt-10 px-3 @2xl:px-10">
			{/* Selected Image Display */}
			<div className="relative w-full">
				<button
					className="group relative w-full h-[250px] @2xl:h-[350px] @6xl:h-[500px] @7xl:h-[600px]"
					aria-label="Enlarge image"
					onClick={() => setIsImageModalOpen(true)}
				>
					<Image
						src={currentGallery?.[activeImageIndex]?.url as string}
						alt={currentGallery?.[activeImageIndex]?.alt ?? 'Product Image'}
						width={150}
						height={150}
						className="object-contain rounded-lg w-full h-full bg-white-frosted-shade"
						sizes="(max-width: 640px) 300px, (max-width: 1024px) 500px, 1000px"
					/>

					{/* Enlarge Icon */}
					<div className="absolute top-2 right-2 bg-white/70 rounded-md p-1 group-hover:bg-white group-hover:text-blue transition-colors duration-300">
						<Icon icon="Enlarge" />
					</div>
				</button>
				{/* Navigation Buttons */}
				<div className="absolute bottom-4 right-2 flex gap-1.5">
					<IconButton
						icon="Arrow"
						variant="solid-white"
						onClick={(e) => {
							e.stopPropagation()
							setActiveImageIndex(updateImageIndex(activeImageIndex, 'prev'))
						}}
						className="rotate-180"
						aria-label="Previous image"
						size="sm"
					/>
					<IconButton
						icon="Arrow"
						variant="solid-white"
						onClick={(e) => {
							e.stopPropagation()
							setActiveImageIndex(updateImageIndex(activeImageIndex, 'next'))
						}}
						aria-label="Next image"
						size="sm"
					/>
				</div>
			</div>

			<Modal isOpen={isImageModalOpen} onClose={() => setIsImageModalOpen(false)}>
				<CarouselInModal
					gallery={currentGallery}
					activeImageIndex={activeImageIndex}
					setActiveImageIndex={setActiveImageIndex}
				/>
			</Modal>

			<div className="flex justify-between flex-wrap gap-2 gap-y-4 mt-4">
				{/* Gallery Thumbnails */}
				<div className="flex gap-2 flex-wrap">
					{currentGallery?.map((image: any, index: number) => (
						<button
							key={index}
							className={clsx(
								'relative w-16 h-16 rounded-md overflow-hidden bg-white-frosted border-2 transition-colors duration-300 ease-in-out',
								activeImageIndex === index ? 'border-blue' : 'border-blue/0'
							)}
							onClick={() => setActiveImageIndex(index)}
						>
							<Image
								src={image.url}
								alt={image.alt ?? `Gallery image ${index + 1}`}
								width={150}
								height={150}
								className="object-cover h-full w-full"
								sizes="64px"
							/>
						</button>
					))}
				</div>

				{/* Only render Toggle if both loaded and unloaded images exist */}
				{product.loaded?.length > 0 && product.unloaded?.length > 0 && (
					<Toggle
						items={['Unloaded', 'Loaded']}
						className="h-fit"
						selected={activeGalleryType}
						onChange={(value: string[]) => {
							setActiveGalleryType(value)
							setActiveImageIndex(1)
						}}
					/>
				)}
			</div>
		</div>
	)
}
